import { trackProductAction } from '@/libs/product-analytics';
import { useAuth } from '@/contexts/auth-context';
import { brandDnaQueryKey } from '@/hooks/queries/use-brand-dna-query';
import type { DnaGeneration } from '@/pages/onboarding/_components/onboarding-dna';
import { useMutation, useMutationState, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { brandDnaLanguage, requestBrandDna } from './use-generate-brand-dna-action';
import { saveBrandDna } from './use-update-brand-dna-action';

const ONBOARDING_DNA_MUTATION_KEY = ['onboarding', 'brandDna'] as const;

/**
 * Reads the brand's website into its Brand DNA and saves it, in one go, from
 * step 1 of the onboarding. It runs on in the background once the brand moved
 * on: the reading and the save are one mutation, so leaving the step does not
 * drop the save. Silent on failure: an unreadable site never blocks the
 * onboarding, the step says so and the companion's Brand DNA gate takes it up.
 */
export function useOnboardingDnaAction() {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const brandId = user?.currentBrandId ?? '';

  return useMutation<string, Error, string>({
    mutationKey: ONBOARDING_DNA_MUTATION_KEY,
    mutationFn: async (websiteUrl) => {
      return trackProductAction('brand_dna', 'create', async () => {
        const response = await requestBrandDna({ websiteUrl }, brandDnaLanguage(i18n.language));
        const dna = response.data.dna;
        await saveBrandDna({ dna, sourceUrl: websiteUrl });
        return dna;
      });
    },
    onSettled: () => {
      if (brandId) {
        queryClient.invalidateQueries({ queryKey: brandDnaQueryKey(brandId) });
      }
    },
  });
}

/** The last reading started from the onboarding, wherever the brand is now. */
export function useLastOnboardingDnaGeneration(): DnaGeneration | undefined {
  const generations = useMutationState({
    filters: { mutationKey: ONBOARDING_DNA_MUTATION_KEY },
    select: (mutation): DnaGeneration => ({
      status: mutation.state.status,
      websiteUrl: (mutation.state.variables as string | undefined) ?? null,
    }),
  });

  return generations.at(-1);
}
